/**
 * Design: Legislation Row (screenshots/25, 31–33). Record + title + sub · status (chip + when) · a
 * "signal" column. The signal is the only thing that changes per tab: a forecast, a recorded vote,
 * a backtest, or the roll-call margin bar ("MARGIN · 17 APART").
 */
import { memo } from "react";
import { ScrollText } from "lucide-react";
import { ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { ForecastChip, chipText } from "../forecast/ForecastViews";
import type { ForecastLabel } from "../forecast/forecast";
import { margin, rowSignal, wardSaid, type Perspective } from "./logic";
import type { LegislationSummary } from "./types";

export type Tone = "neutral" | "positive" | "warning" | "negative";

export const statusTone = (status: string): Tone =>
  /^Deferred/.test(status) ? "warning" : /^(Passed|Adopted|Signed)/.test(status) ? "positive" : /^Failed/.test(status) ? "negative" : "neutral";

export type Signal =
  | { kind: "forecast"; label: string; forecastLabel: ForecastLabel; perspective: Perspective; tally?: string; p?: number }
  | { kind: "vote"; label: string; vote: "yea" | "nay" | "absent" }
  | { kind: "backtest"; label: string; text: string; missed?: boolean }
  | { kind: "margin"; yea: number; nay: number }
  | { kind: "wardVoice"; ward: number; support: number; oppose: number; minResponses?: number };

export const href = (recordId: string) => `/dashboard/legislation/${encodeURIComponent(recordId)}`;

/** Upcoming items: the council forecast, or "Not enough history" when forecastVote() had too little to go on. */
export function forecastSignal(it: LegislationSummary): Signal {
  if (!it.forecast) return { kind: "backtest", label: "Forecast", text: "Not enough history" };
  const s = rowSignal(it.forecast, it.myPosition);
  return { kind: "forecast", label: s.label, forecastLabel: s.forecastLabel, perspective: s.perspective, tally: s.tally };
}

/** Decided items: what we forecast the day before vs. what happened. */
export const backtestSignal = (it: LegislationSummary): Signal => it.backtest
  ? { kind: "backtest", label: "Our forecast", missed: it.backtest.missed,
      text: `${it.backtest.projectedYes}–${it.backtest.projectedNo} · ${it.backtest.missed ? "missed" : it.backtest.missBy === 0 ? "exact" : `off by ${it.backtest.missBy}`}` }
  : { kind: "backtest", label: "Our forecast", text: "Not tracked then" };

/** "Passed 32–15" once voted, otherwise the status ("Deferred & published"). */
export const outcomeOf = (it: LegislationSummary) => it.rollCall
  ? `${it.rollCall.outcome === "failed" ? "Failed" : it.rollCall.outcome === "adopted" ? "Adopted" : "Passed"} ${it.rollCall.yea}–${it.rollCall.nay}`
  : it.status;

export const LegislationRow = memo(function LegislationRow({ item, sub, signal, neutralOutcome }: {
  item: LegislationSummary; sub?: string; signal: Signal; neutralOutcome?: boolean;
}) {
  const outcome = outcomeOf(item);
  const tone = neutralOutcome && !/^Failed/.test(outcome) ? "neutral" : statusTone(outcome);
  return (
    // tabIndex -1: j/k (useListKeyboard) move focus to the row; its title is the link and the tab stop
    <ListRow className="leg-row" href={href(item.recordId)} data-row={item.recordId} tabIndex={-1}>
      <RowMain min={160} className="leg-row__main">
        <span className="leg-row__meta">
          <i className="type-badge" data-type="legislation" style={{ width: 18, height: 18 }} aria-hidden><ScrollText size={11} /></i>
          <span className="leg-row__record">{item.recordId}</span><span>{item.category}</span>
        </span>
        <RowTitle className="leg-row__title">{item.title}</RowTitle>
        <RowSub wrap className="leg-row__sub">{sub ?? item.sub}</RowSub>
      </RowMain>
      <RowCell width={170} className="leg-row__status">
        <span className={`chip chip--${tone} lg-status`}>{outcome}</span>
        <span className="leg-row__when">{item.rollCall?.date ?? item.statusWhen}</span>
      </RowCell>
      <RowCell width={170} className="leg-row__signal"><SignalCell signal={signal} /></RowCell>
    </ListRow>
  );
});

function SignalCell({ signal }: { signal: Signal }) {
  switch (signal.kind) {
    case "forecast":
      return (<>
        <span className="eyebrow">{signal.label}</span>
        <ForecastChip label={signal.forecastLabel} perspective={signal.perspective} p={signal.tally ? undefined : signal.p}
          text={signal.tally ? `${chipText(signal.forecastLabel, signal.perspective)} · ${signal.tally}` : undefined} />
      </>);
    case "vote":
      return (<><span className="eyebrow">{signal.label}</span>
        <span className={`chip chip--${signal.vote === "yea" ? "positive" : signal.vote === "nay" ? "negative" : "neutral"}`}>{signal.vote.toUpperCase()}</span></>);
    case "backtest":
      return (<><span className="eyebrow">{signal.label}</span><span className={`chip lg-backtest chip--${signal.missed ? "warning" : "neutral"}`}>{signal.text}</span></>);
    case "margin":
      return <MarginBar yea={signal.yea} nay={signal.nay} />;
    case "wardVoice": {
      const w = wardSaid(signal, signal.minResponses);
      return (<><span className="eyebrow">Ward {signal.ward} said</span>
        <span className={`chip chip--${w.tone} lg-ward-chip`}>{w.enough ? `${w.share}% support · ${w.n}` : `Not enough yet · ${w.n}`}</span></>);
    }
  }
}

/** Roll-calls tab: yea share vs. nay share, "MARGIN · 17 APART". */
export function MarginBar({ yea, nay }: { yea: number; nay: number }) {
  const m = margin(yea, nay);
  return (
    <span className="margin" role="img" aria-label={`${yea} yea, ${nay} nay, ${m.apart} apart`}>
      <span className="eyebrow">Margin · {m.apart} apart</span>
      <span className="margin__bar"><span className="is-yea" style={{ flexGrow: m.yeaShare }} /><span className="is-nay" style={{ flexGrow: 1 - m.yeaShare }} /></span>
    </span>
  );
}
